import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import config from "../common/Config";
import generateReturnPDFRhp from "./generateReturnPDFRhp";
import "../common/commonStyles.css";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "minimal-ui";

const isRhpTenant = () =>
  config.PREFIX === "rhp" ||
  localStorage.getItem("accessToken") === "rhp" ||
  localStorage.getItem("accessToken") === "hp";

const formatDate = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    const raw = String(timestamp);
    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[3]}-${match[2]}-${match[1]}`;
    return raw;
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${day}-${month}-${year}`;
};

const getItemVatCode = (item) =>
  String(item.vat_code ?? item.tax_code ?? item.tax_id ?? "") || "-";

const getItemTax = (item) =>
  parseFloat(item.tax ?? item.item_tax ?? item.vat_amount ?? 0) || 0;

const getItemSubtotal = (item) => parseFloat(item.subtotal ?? 0) || 0;

const getItemNet = (item) => {
  const subtotal = getItemSubtotal(item);
  const tax = getItemTax(item);
  const explicitNet = parseFloat(item.net ?? item.net_amount ?? NaN);
  if (Number.isFinite(explicitNet)) return explicitNet;
  if (subtotal >= tax) return subtotal - tax;
  return subtotal;
};

const normalizeDiscountType = (type) => {
  const value = String(type ?? "amount").trim().toLowerCase();
  return value === "percentage" ? "percentage" : "amount";
};

const formatItemDiscountDisplay = (item, currencySymbol = "£") => {
  const itemDiscount = parseFloat(item.item_discount ?? 0) || 0;
  const discountValue = parseFloat(item.discount ?? 0) || 0;
  const discountType = normalizeDiscountType(
    item.item_discount_type ?? item.discount_type
  );

  if (discountType === "percentage" && discountValue > 0) {
    const pct =
      Number.isInteger(discountValue) || discountValue % 1 === 0
        ? String(parseInt(discountValue, 10))
        : discountValue.toFixed(2).replace(/\.?0+$/, "");
    return `${pct}%`;
  }

  if (itemDiscount > 0) {
    return `${currencySymbol}${itemDiscount.toFixed(2)}`;
  }

  if (discountValue > 0 && discountType === "amount") {
    return `${currencySymbol}${discountValue.toFixed(2)}`;
  }

  return "-";
};

const buildVatAnalysis = (items = []) => {
  const groups = new Map();
  items.forEach((item) => {
    const code = String(item.vat_code ?? item.tax_code ?? item.tax_id ?? "");
    const rate = parseFloat(
      item.vat_rate ?? item.tax_rate_percent ?? item.tax_rate ?? 0
    );
    const safeRate = Number.isFinite(rate) ? rate : 0;
    const name =
      item.vat_name ||
      item.tax_name ||
      (safeRate === 0 ? "Zero Rated" : "Std Rate");
    const key = `${code}|${safeRate}|${name}`;
    if (!groups.has(key)) {
      groups.set(key, {
        code: code || "-",
        rate: safeRate,
        name,
        net: 0,
        vat: 0,
      });
    }
    const row = groups.get(key);
    row.net += getItemNet(item);
    row.vat += getItemTax(item);
  });
  return Array.from(groups.values()).sort((a, b) =>
    String(a.code).localeCompare(String(b.code), undefined, { numeric: true })
  );
};

const ReturnListPrintRhp = () => {
  const { orderId } = useParams();
  const [shopDetails, setShopDetails] = useState({});
  const [orderDetails, setOrderDetails] = useState({});
  const [productDetails, setProductDetails] = useState([]);
  const accessToken = localStorage.getItem("accessToken");
  const currencySymbol = localStorage.getItem("currencySymbol") || "£";
  const company_tax_type = localStorage.getItem("company_tax_type") || "VAT";
  const formatCurrency = (value) =>
    `${currencySymbol}${parseFloat(value || 0).toFixed(2)}`;
  const formatPlain = (value) => parseFloat(value || 0).toFixed(2);

  const vatAnalysis = useMemo(
    () => buildVatAnalysis(productDetails),
    [productDetails]
  );
  const totalNet = vatAnalysis.reduce((sum, row) => sum + row.net, 0);
  const totalVat = vatAnalysis.reduce((sum, row) => sum + row.vat, 0);
  const totalGross =
    parseFloat(orderDetails?.grand_total) > 0
      ? parseFloat(orderDetails.grand_total)
      : totalNet + totalVat;

  const fetchInvoice = async () => {
    const formData = new FormData();
    formData.append("id", orderId);
    try {
      const response = await fetch(`${config.API_URL}/return/returnView`, {
        method: "POST",
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });
      if (!response.ok) throw new Error("Failed to fetch return");
      const data = await response.json();
      setShopDetails(data.data?.shop_details || {});
      setOrderDetails(data.data?.commonData || {});
      setProductDetails(
        Array.isArray(data.data?.itemData) ? data.data.itemData : []
      );
    } catch (error) {}
  };

  useEffect(() => {
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
    if (!isRhpTenant()) {
      window.location.href = `${config.BASE_URL}/returns/view/${orderId}`;
      return;
    }
    fetchInvoice();
  }, []);

  return (
    <Box className="page-container">
      <Paper className="paper-style">
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, m: 2 }}>
          <Button
            variant="contained"
            className="button-variant-contained"
            onClick={() => generateReturnPDFRhp(orderId)}
          >
            Print
          </Button>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Box
            className="row"
            sx={{ pb: 2, borderBottom: "1px solid #ccc" }}
          >
            <Box className="col-7">
              <Typography display="flex" flexDirection="column">
                <Typography variant="body2" fontWeight={500}>
                  {shopDetails?.shop_name}
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  TEL :{shopDetails?.phone}
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {company_tax_type} NO:
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  COMPANY REG NO : {shopDetails?.company_reg_no}
                </Typography>
              </Typography>
            </Box>
            <Box className="col-5">
              <Box sx={{ pl: 2, m: 0 }} display="flex" flexDirection="column">
                <Typography variant="body2" fontWeight={500}>
                  PLEASE PHONE THE OFFICE TO PAY BY CREDIT/DEBIT CARD
                </Typography>
                <Typography display="flex" flexDirection="row" gap={1}>
                  <Typography variant="body2" fontWeight={500}>
                    Bank A/C :
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {shopDetails?.bank_ac}
                  </Typography>
                </Typography>
                <Typography display="flex" flexDirection="row" gap={1}>
                  <Typography variant="body2" fontWeight={500}>
                    Sort Code :
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {shopDetails?.sort_code}
                  </Typography>
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box className="row" sx={{ pt: 2 }}>
            <Box className="col-7">
              <Typography>
                <Typography variant="body2" fontWeight={500}>
                  {orderDetails?.company}
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {orderDetails?.address || orderDetails?.line1}
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {orderDetails?.city}, {orderDetails?.postal_code},{" "}
                  {orderDetails?.country}
                </Typography>
              </Typography>
            </Box>
            <Box className="col-5">
              <Box display="flex" flexDirection="column">
                <Typography display="flex" flexDirection="row" gap={1}>
                  <Typography variant="body2" fontWeight={500}>
                    Credit Note No:
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    INV{orderDetails?.reference_no}
                  </Typography>
                </Typography>
                <Typography display="flex" flexDirection="row" gap={1}>
                  <Typography variant="body2" fontWeight={500}>
                    Credit Date:
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {formatDate(orderDetails?.date)}
                  </Typography>
                </Typography>
                <Typography display="flex" flexDirection="row" gap={1}>
                  <Typography variant="body2" fontWeight={500}>
                    Cust Account No:
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {orderDetails?.accound_no || orderDetails?.reference_no}
                  </Typography>
                </Typography>
              </Box>
            </Box>
          </Box>

          <TableContainer sx={{ mt: 3 }}>
            <Table
              sx={{
                tableLayout: "fixed",
                width: "100%",
                "& .MuiTableCell-root": {
                  border: "none",
                  color: "#000",
                  fontSize: "0.8rem",
                  py: 0.75,
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: "32%", fontWeight: 700 }}>
                    Description
                  </TableCell>
                  <TableCell sx={{ width: "8%", fontWeight: 700 }} align="center">
                    Quantity
                  </TableCell>
                  <TableCell sx={{ width: "8%", fontWeight: 700 }} align="center">
                    Type
                  </TableCell>
                  <TableCell sx={{ width: "12%", fontWeight: 700 }} align="right">
                    Unit Price
                  </TableCell>
                  <TableCell sx={{ width: "10%", fontWeight: 700 }} align="right">
                    Discount
                  </TableCell>
                  <TableCell sx={{ width: "12%", fontWeight: 700 }} align="right">
                    Net Amount
                  </TableCell>
                  <TableCell sx={{ width: "10%", fontWeight: 700 }} align="center">
                    VAT Code
                  </TableCell>
                  <TableCell sx={{ width: "8%", fontWeight: 700 }} align="center">Return Type</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {productDetails.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      {item.product_code} - {item.product_name}
                    </TableCell>
                    <TableCell align="center">{item.quantity}</TableCell>
                    <TableCell align="center">{item.return_type}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(item.unit_price)}
                    </TableCell>
                    <TableCell align="right">
                      {formatItemDiscountDisplay(item, currencySymbol)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(getItemNet(item))}
                    </TableCell>
                    <TableCell align="center" sx={{ color: "#000" }}>
                      {getItemVatCode(item)}
                    </TableCell>
                    <TableCell align="center">{item.returnReason}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              mt: 3,
              display: "flex",
              justifyContent: "space-between",
              gap: 4,
              flexWrap: "wrap",
              px: 0.5,
            }}
          >
            <Box sx={{ minWidth: 340, flex: 1, fontFamily: "monospace" }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "48px 56px 0.2fr 72px 72px",
                  columnGap: 1,
                  mb: 0.5,
                }}
              >
                <Typography variant="body2" fontWeight={700}>
                  Code
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  %
                </Typography>
                <Typography variant="body2" fontWeight={700} />
                <Typography variant="body2" fontWeight={700}>
                  Net
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  VAT
                </Typography>
              </Box>
              {vatAnalysis.map((row) => (
                <Box
                  key={`${row.code}-${row.rate}-${row.name}`}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "48px 56px 0.2fr 72px 72px",
                    columnGap: 1,
                    mb: 0.25,
                  }}
                >
                  <Typography variant="body2" sx={{ color: "#000" }}>
                    {row.code}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#000" }}>
                    {formatPlain(row.rate)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#000" }}>
                    {row.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#000" }}>
                    {formatPlain(row.net)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#000" }}>
                    {formatPlain(row.vat)}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                minWidth: 160,
                display: "flex",
                flexDirection: "column",
                flexWrap: "wrap",
                justifyContent: "flex-end",
                gap: 0.2,
                pr: 1,
              }}
            >
              <Typography display="flex" flexDirection="row" gap={0.2} variant="body2" fontWeight={700} sx={{ color: "#000" }}>
                NET&nbsp;&nbsp; <Typography variant="body2" sx={{ color: "#000" }}>{formatPlain(totalNet)}</Typography>
              </Typography>
              <Typography display="flex" flexDirection="row" gap={0.2} variant="body2" fontWeight={700} sx={{ mt: 0.5, color: "#000" }}>
                VAT&nbsp;&nbsp; <Typography variant="body2" sx={{ color: "#000" }}>{formatPlain(totalVat)}</Typography>
              </Typography>
              <Typography display="flex" flexDirection="row" gap={0.2} variant="body2" fontWeight={700} sx={{ mt: 0.5, color: "#000" }}>
                GROSS&nbsp;&nbsp; <Typography variant="body2" sx={{ color: "#000" }}>{formatPlain(totalGross)}</Typography>
              </Typography>
            </Box>
          </Box>

          <Typography className="invoice-footer-text">
            No claims for damages or short delivery will be considered unless
            made at the time of delivery of goods. These goods are not sold on a
            sale or return basis.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default ReturnListPrintRhp;
